/**
 * The bell (canvas defect #64): the signed-in person's notices from `/comms/notifications`, one
 * per event, read on open and again whenever the live stream says a notification landed or the
 * connection came back (ADR 0008 — no polling); mark done drops the row from the list and the
 * count. Null until the first answer — the badge reads NO DATA, never a calm 0.
 */
import { useCallback, useEffect, useState } from 'react';
import { useLive } from '@cider/ui';
import { api } from './api.js';

export interface Notice {
  id: string;
  kind: string;
  title: string;
  href: string | null;
  about: string | null;
  createdAt: string;
}

export function useNotices(signedIn: boolean): {
  notices: Notice[] | null;
  count: number | null;
  reload: () => Promise<void>;
  done: (id: string) => Promise<void>;
} {
  const [notices, setNotices] = useState<Notice[] | null>(null);
  const reload = useCallback(async () => {
    if (!signedIn) {
      setNotices(null);
      return;
    }
    try {
      const r = await api<{ count: number; notifications: Notice[] }>(
        'GET',
        '/comms/notifications',
      );
      setNotices(r.notifications);
    } catch {
      setNotices(null);
    }
  }, [signedIn]);
  useEffect(() => {
    void reload();
  }, [reload]);
  useLive(signedIn ? '/api/events' : null, ['comms.notification'], () => void reload());
  const done = useCallback(async (id: string) => {
    await api('POST', `/comms/notifications/${id}/done`);
    setNotices((n) => (n ? n.filter((x) => x.id !== id) : n));
  }, []);
  return { notices, count: notices ? notices.length : null, reload, done };
}
